import {
  FolderOpen,
  Server,
  UserPlus,
  Globe,
  ShoppingCart,
  Code,
  FileText,
  Database,
  CloudLightning,
} from "lucide-react";

export const DETECTION_RULES: Record<string, RegExp[]> = {
  ga4: [/ga4/i, /ua-/i, /analytics/i],
  gtm: [/gtm/i, /google\s?tag\s?manager/i],
  google_ads: [/google\s?ads/i, /adwords/i],
  meta: [
    /meta/i,
    /facebook/i,
    /fb\s?ads/i,
    /instagram/i,
    /ig\s?ads/i,
    /capi/i,
    /pixel/i,
  ],
  tiktok: [/tiktok/i, /tt\s?ads/i, /pangle/i],
  pinterest: [/pinterest/i, /pin\s?tag/i],
  snapchat: [/snapchat/i, /snap\s?pixel/i],
  linkedin: [/linkedin/i, /insight\s?tag/i],
  microsoft_ads: [/microsoft/i, /bing/i],
  shopify: [/shopify/i],
  woocommerce: [/woocommerce/i, /woo\s?commerce/i, /wordpress/i],
  magento: [/magento/i, /adobe\s?commerce/i],
  lightspeed: [/lightspeed/i],
  phoenix: [/phoenix/i],
  cookiebot: [/cookiebot/i],
  cookieyes: [/cookieyes/i],
  iubenda: [/iubenda/i],
  axeptio: [/axeptio/i],
};

export const generateChecklists = (data: any) => {
  const {
    wantsNotion,
    containerId,
    serviceType,
    cmps,
    websiteType,
    platforms,
    customScope,
    customTasks,
    customCmps,
    customPlatforms,
  } = data;

  const admin = [
    {
      id: "notion_create",
      label: "Notion Board Created & Access Shared",
      icon: FolderOpen,
      condition: () => wantsNotion,
      role: "pm",
    },
    {
      id: "client_account",
      label: "Client TAGGRS Account Active",
      icon: Server,
      required: true,
      externalLink: containerId
        ? `https://dashboard.taggrs.io/products/${containerId}`
        : null,
      role: "pm", // PM checks if active
    },
    {
      id: "container_create",
      label: "Container Created",
      condition: () => serviceType !== "audit",
      role: "customer", // Customer creates it
      hasIdField: true,
      idLabel: "Container ID",
    },
    {
      id: "container_access",
      label: "Access: mcc@taggrs.io added to Container",
      role: "pm", // PM verifies access
    },
    {
      id: "partner_added",
      label: "Partner added to the container",
      icon: UserPlus,
      condition: () => serviceType === "implementation",
      role: "pm",
    },
  ];

  const infrastructure = [
    {
      id: "dns_sst",
      label: 'DNS: "sst" Subdomain Created & Validated',
      condition: () => serviceType === "implementation",
      role: "customer", // Customer creates DNS
    },
    // CMP TASKS
    {
      id: "cookiebot_access",
      label: "Cookiebot Access",
      condition: () => cmps?.cookiebot,
      role: "customer",
    },
    {
      id: "cookieyes_access",
      label: "CookieYes Access",
      condition: () => cmps?.cookieyes,
      role: "customer",
    },
    {
      id: "iubenda_access",
      label: "iubenda Access",
      condition: () => cmps?.iubenda,
      role: "customer",
    },
    {
      id: "axeptio_access",
      label: "Axeptio Access",
      condition: () => cmps?.axeptio,
      role: "customer",
    },
    ...(customCmps || []).map((cp: string) => ({
      id: `${cp}_access`,
      label: `${cp} Access`,
      condition: () => cmps?.[cp],
      role: "customer",
    })),
  ];

  const website = [
    {
      id: "web_dev",
      label: "Web Developer Contact Received",
      icon: Globe,
      condition: () => serviceType === "implementation",
      role: "customer", // Customer provides this
    },
    {
      id: "discount_code",
      label: "100% Discount Code (Test Orders)",
      icon: ShoppingCart,
      condition: () =>
        (serviceType === "implementation" || serviceType === "audit") &&
        websiteType === "ecommerce",
      role: "customer",
    },
    {
      id: "cms_access",
      label: "CMS Access (Shopify/Woo/etc)",
      condition: () => serviceType === "implementation",
      role: "customer",
    },
  ];

  const platformTasks = [
    {
      id: "gtm_access",
      label: "GTM Publish Access",
      condition: () => platforms?.gtm,
      hasIdField: true,
      idLabel: "GTM ID",
      role: "customer",
      instructionKey: "gtm_access",
    },
    {
      id: "ga4_access",
      label: "GA4 Admin Access",
      condition: () => platforms?.ga4,
      hasIdField: true,
      idLabel: "Measurement ID",
      role: "customer",
      instructionKey: "ga4_access",
    },
    {
      id: "gads_access",
      label: "Google Ads Link/Access",
      condition: () => platforms?.google_ads,
      hasIdField: true,
      idLabel: "Ads Account ID",
      role: "customer",
      instructionKey: "gads_access",
    },
    {
      id: "meta_access",
      label: "Meta Business Manager Access",
      condition: () => platforms?.meta,
      hasIdField: true,
      idLabel: "Business ID",
      role: "customer",
      instructionKey: "meta_access",
    },
    {
      id: "tiktok_access",
      label: "TikTok Ads Access",
      condition: () => platforms?.tiktok,
      role: "customer",
      instructionKey: "tiktok_access",
    },
    {
      id: "pinterest_access",
      label: "Pinterest Tag Access",
      condition: () => platforms?.pinterest,
      role: "customer",
      instructionKey: "pinterest_access",
    },
    {
      id: "snap_access",
      label: "Snapchat Ads Access",
      condition: () => platforms?.snapchat,
      role: "customer",
      instructionKey: "snap_access",
    },
    {
      id: "linkedin_access",
      label: "LinkedIn Insight Tag Access",
      condition: () => platforms?.linkedin,
      role: "customer",
      instructionKey: "linkedin_access",
    },
    {
      id: "microsoft_ads_access",
      label: "Microsoft Ads Access",
      condition: () => platforms?.microsoft_ads,
      role: "customer",
      instructionKey: "microsoft_ads_access",
    },
    ...(customPlatforms || []).map((cp: string) => ({
      id: `${cp}_access`,
      label: `${cp} Access`,
      condition: () => platforms?.[cp],
      role: "customer",
    })),
  ];

  const advanced = [
    {
      id: "scope_datalayer",
      label: "Custom Datalayers Defined",
      icon: Code,
      condition: () => customScope?.datalayers,
      role: "pm",
    },
    {
      id: "dl_forms",
      label: "Access to Forms",
      icon: FileText,
      condition: () => customScope?.datalayers,
      role: "customer",
    },
    {
      id: "dl_backend",
      label: "Backend Access",
      icon: Database,
      condition: () => customScope?.datalayers,
      role: "customer",
    },
    {
      id: "scope_offline",
      label: "Offline Conversions Setup",
      icon: CloudLightning,
      condition: () => customScope?.offline_conv,
      role: "pm",
    },
    {
      id: "oc_gcloud",
      label: "Google Cloud Access",
      icon: Server,
      condition: () => customScope?.offline_conv,
      role: "customer",
    },
    {
      id: "oc_firestore",
      label: "Firestore Access",
      icon: Database,
      condition: () => customScope?.offline_conv,
      role: "customer",
    },
    {
      id: "oc_devtoken",
      label: "Google Ads Developer Token",
      icon: Code,
      condition: () => customScope?.offline_conv,
      role: "customer",
    },
  ];

  return {
    admin: [
      ...admin.filter((i) => !i.condition || i.condition()),
      ...(customTasks?.admin || []),
    ],
    infrastructure: [
      ...infrastructure.filter((i) => !i.condition || i.condition()),
      ...(customTasks?.infrastructure || []),
    ],
    website: [
      ...website.filter((i) => !i.condition || i.condition()),
      ...(customTasks?.website || []),
    ],
    platforms: [
      ...platformTasks.filter((i) => !i.condition || i.condition()),
      ...(customTasks?.platforms || []),
    ],
    advanced: [
      ...advanced.filter((i) => !i.condition || i.condition()),
      ...(customTasks?.advanced || []),
    ],
    custom: customTasks?.custom || [],
  };
};
